import { useEffect, useMemo, useRef, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import {
  type ActivityNextStepAction,
  ActivityNextStepsDialog,
} from '@modules/_shared/components/feed/ActivityNextStepsDialog';
import type {
  ActivityFollowUpContext,
  OutcomeFollowUpAction,
} from '@modules/_shared/components/feed/activityOutcomeShared';
import { createActivityNextStepOptions } from '@modules/_shared/components/feed/activityOutcomeShared';
import type { PipelineLeadType } from '@modules/_shared/components/feed/types';
import { GET_PENDING_ACTIVITIES } from '@modules/_shared/components/feed/utils/activityQueries';
import { createNextStepsCloseHandler } from '@modules/_shared/components/feed/utils/outcomeDialogHelpers';
import { RaDate } from '@modules/_shared/components/form/RaDate';
import {
  RaSelect,
  type RaSelectOption,
} from '@modules/_shared/components/form/RaSelect';
import { MarkdownComposer } from '@modules/_shared/components/MarkdownComposer';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import type { PendingPlanActivity } from '@modules/lead-details/hooks/usePlanActivityConflict';
import { GET_LEAD_AGENTS } from '@modules/leads/leadsQueries';
import { useLocale } from '@shared/hooks/locale';
import { number_of_string } from '@shared/utils/number-format';

const parseNumeric = (value: string | null | undefined) => {
  if (!value) {
    return null;
  }

  // Try parsing after stripping non-numeric characters (except dots and dashes)
  const numeric = Number.parseFloat(value.replace(/[^0-9.-]+/g, ''));
  if (!Number.isNaN(numeric)) {
    return numeric;
  }

  // Fallback: try parsing the original value (handles special cases like "Infinity")
  const fallback = Number.parseFloat(value);
  return Number.isNaN(fallback) ? null : fallback;
};

const GET_VISIT_ACTIVITY = gql(/* GraphQL */ `
  query VisitOutcomeDialogFlowActivity($id: uuid!) {
    activities_by_pk(id: $id) {
      id
      note
      assigned_to
      lead {
        id
        lead_agents(where: { passed_at: { _is_null: true } }) {
          id
          user_id
          win_probability
          predicted_win_date
          lead_stage {
            id
            status
            pipeline {
              id
              name
              lead_type
            }
          }
        }
      }
    }
    mandate_probability_types: dictionaries(
      where: { type: { _eq: mandate_probability_types } }
      order_by: { label: asc }
    ) {
      name
      label
    }
  }
`);

const COMPLETE_VISIT_AND_UPDATE_LEAD_AGENT = gql(/* GraphQL */ `
  mutation VisitOutcomeDialogFlowCompleteVisitAndUpdateLeadAgent(
    $activityId: uuid!
    $success: Boolean!
    $done_at: timestamptz!
    $note: String
    $shouldUpdateLeadAgent: Boolean!
    $leadAgentId: uuid!
    $win_probability: Int
    $predicted_win_date: date
  ) {
    update_activities_by_pk(
      pk_columns: { id: $activityId }
      _set: { done: true, success: $success, done_at: $done_at, note: $note }
    ) {
      id
    }
    update_lead_agents_by_pk(
      pk_columns: { id: $leadAgentId }
      _set: {
        win_probability: $win_probability
        predicted_win_date: $predicted_win_date
      }
    ) @include(if: $shouldUpdateLeadAgent) {
      id
    }
  }
`);

type VisitOutcomeFormValues = {
  note: string;
  win_probability: string;
  predicted_win_date: string | null;
};

type VisitOutcomeFollowUpAction = Extract<
  OutcomeFollowUpAction,
  | 'schedule_visit'
  | 'schedule_call'
  | 'schedule_task'
  | 'lead_lost'
  | 'lead_won'
  | 'do_nothing'
>;

type VisitOutcomeDialogFlowProps = {
  open: boolean;
  activity: PendingPlanActivity | null;
  onClose: () => void;
  onFollowUpActionSelect?: (action: VisitOutcomeFollowUpAction) => void;
  canMarkLeadLost?: boolean;
  onCancel?: () => void;
  autoOutcome?: 'completed' | 'cancelled' | null;
  parentType?: null | 'Enquiry' | 'Lot' | 'Lead' | 'User' | 'BuyerLead';
  activityId?: string | null;
  pipelineType: PipelineLeadType;
  onLoadingChange?: (
    loading: boolean,
    outcomeType: 'completed' | 'cancelled' | null,
  ) => void;
};

const resolveVisitStageFromOutcome = (
  outcome: VisitOutcomeDialogFlowProps['autoOutcome'],
) => {
  if (outcome === 'completed') {
    return 'completed';
  }
  if (outcome === 'cancelled') {
    return 'cancelled';
  }
  return 'outcome';
};

const normalizePredictedWinDate = (dateValue: string | null): string | null => {
  if (!dateValue) {
    return null;
  }

  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  // Format as date string (YYYY-MM-DD) using local date to avoid timezone shifts
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const VisitOutcomeDialogFlow = ({
  open,
  activity,
  activityId = null,
  onClose,
  onFollowUpActionSelect,
  canMarkLeadLost = false,
  onCancel,
  autoOutcome = null,
  parentType,
  pipelineType,
  onLoadingChange,
}: VisitOutcomeDialogFlowProps) => {
  const { t } = useLocale();
  const theme = useTheme();
  const { me } = useAppData();
  const [errorLike, setErrorLike] = useState<ErrorLike | null>(null);
  const [stage, setStage] = useState<'outcome' | 'completed' | 'cancelled'>(
    resolveVisitStageFromOutcome(autoOutcome),
  );
  const [nextStepsContext, setNextStepsContext] =
    useState<ActivityFollowUpContext<PendingPlanActivity | null> | null>(null);
  const selectedActionRef = useRef<VisitOutcomeFollowUpAction | null>(null);

  const effectiveActivityId = activity?.id ?? activityId ?? null;

  const { control, reset, clearErrors, handleSubmit } =
    useForm<VisitOutcomeFormValues>({
      defaultValues: {
        note: '',
        win_probability: '',
        predicted_win_date: null,
      },
    });

  const currentOutcomeRef = useRef<'completed' | 'cancelled' | null>(
    autoOutcome ?? null,
  );

  const {
    data,
    loading: isLoadingActivity,
    error: activityError,
  } = useQuery(GET_VISIT_ACTIVITY, {
    skip: !open || !effectiveActivityId,
    variables: { id: effectiveActivityId! },
  });

  // Apollo v4 removed `onError` from `useQuery`; surface query errors via the
  // same snackbar that mutation onError callbacks feed.
  useEffect(() => {
    if (activityError) {
      setErrorLike(activityError);
    }
  }, [activityError]);

  const [completeVisitAndUpdateLeadAgent, { loading }] = useMutation(
    COMPLETE_VISIT_AND_UPDATE_LEAD_AGENT,
    {
      refetchQueries: [GET_PENDING_ACTIVITIES, GET_LEAD_AGENTS],
      awaitRefetchQueries: true,
      onError: setErrorLike,
    },
  );

  useEffect(() => {
    // Ensure ref is set from autoOutcome before checking loading state
    if (autoOutcome && currentOutcomeRef.current !== autoOutcome) {
      currentOutcomeRef.current = autoOutcome;
    }

    if (isLoadingActivity && currentOutcomeRef.current && autoOutcome) {
      onLoadingChange?.(isLoadingActivity, currentOutcomeRef.current);
    } else if (!isLoadingActivity && autoOutcome) {
      onLoadingChange?.(false, null);
    }
  }, [isLoadingActivity, onLoadingChange, autoOutcome]);

  useEffect(() => {
    if (autoOutcome) {
      currentOutcomeRef.current = autoOutcome;
    } else {
      currentOutcomeRef.current = null;
    }
  }, [autoOutcome]);

  const activityData = data?.activities_by_pk;
  const lead = activityData?.lead;
  const assignedAgentId = activityData?.assigned_to;

  const assignedLeadAgent = lead?.lead_agents?.find(
    agent => agent.user_id === assignedAgentId,
  );

  const initialWinProbability =
    assignedLeadAgent?.win_probability != null
      ? String(assignedLeadAgent.win_probability)
      : '';

  const initialPredictedWinDate = assignedLeadAgent?.predicted_win_date ?? null;

  const tenantCountry = me?.tenant?.country_code ?? null;

  const isBrokeragePipeline = pipelineType === 'sales';

  const shouldShowWinFields =
    tenantCountry === 'CH' && isBrokeragePipeline && assignedLeadAgent != null;

  const winProbabilityValue = useWatch({
    control,
    name: 'win_probability',
    defaultValue: '',
  });

  const isPredictedDateDisabled =
    loading || !shouldShowWinFields || winProbabilityValue === '';

  useEffect(() => {
    if (open && !isLoadingActivity) {
      reset({
        note: activityData?.note ?? '',
        win_probability: '',
        predicted_win_date: null,
      });
      clearErrors();
      setStage(resolveVisitStageFromOutcome(autoOutcome));
      // Reset ref when dialog opens (unless autoOutcome is set)
      if (!autoOutcome) {
        currentOutcomeRef.current = null;
      }
    }
  }, [
    open,
    autoOutcome,
    isLoadingActivity,
    reset,
    clearErrors,
    activityData?.note,
  ]);

  const resetDialogState = (
    reason: 'cancelled' | 'completed' = 'cancelled',
  ) => {
    if (reason === 'cancelled') {
      setStage(resolveVisitStageFromOutcome(autoOutcome));
    }
    clearErrors();
    reset({
      note: activityData?.note ?? '',
      win_probability: '',
      predicted_win_date: null,
    });
    if (reason === 'cancelled') {
      onCancel?.();
      onClose();
    }
  };

  const submitOutcome = async ({
    success,
    data,
  }: {
    success: boolean;
    data: VisitOutcomeFormValues;
  }) => {
    if (!effectiveActivityId) {
      resetDialogState();
      return;
    }

    const note = (data.note ?? '').trim();

    // Prepare lead agent update variables
    const winProbabilityValue = data.win_probability;
    const parsedWinProbability =
      winProbabilityValue !== '' ? number_of_string(winProbabilityValue) : null;

    const predictedWinDateValue = data.predicted_win_date;
    const normalizedPredictedWinDate = normalizePredictedWinDate(
      predictedWinDateValue,
    );
    const sanitizedPredictedWinDate =
      winProbabilityValue === '' || winProbabilityValue === '0'
        ? null
        : normalizedPredictedWinDate;

    // shouldShowWinFields guarantees assignedLeadAgent is not null
    const shouldUpdateLeadAgent =
      shouldShowWinFields &&
      (parsedWinProbability !== assignedLeadAgent!.win_probability ||
        sanitizedPredictedWinDate !== assignedLeadAgent!.predicted_win_date);

    await completeVisitAndUpdateLeadAgent({
      variables: {
        activityId: effectiveActivityId,
        success,
        done_at: new Date().toISOString(),
        note: note.trim().length > 0 ? note.trim() : null,
        shouldUpdateLeadAgent,
        leadAgentId: assignedLeadAgent?.id ?? '',
        win_probability:
          parsedWinProbability != null
            ? Math.trunc(parsedWinProbability)
            : null,
        predicted_win_date: sanitizedPredictedWinDate,
      },
    });

    emitFeedRefresh();
  };

  const handleOutcomeSelection = (nextStage: 'completed' | 'cancelled') => {
    if (isLoadingActivity) {
      return;
    }
    currentOutcomeRef.current = nextStage;
    setStage(nextStage);
    clearErrors();
    if (nextStage === 'completed') {
      reset({
        note: activityData?.note ?? '',
        win_probability: initialWinProbability,
        predicted_win_date: initialPredictedWinDate,
      });
      return;
    }

    reset({
      note: activityData?.note ?? '',
      win_probability: '',
      predicted_win_date: null,
    });
  };

  const handleCompletedSubmit = handleSubmit(async data => {
    if (!effectiveActivityId) {
      resetDialogState();
      return;
    }

    currentOutcomeRef.current = 'completed';
    await submitOutcome({ success: true, data });
    const followUpActivity = activity ?? {
      id: effectiveActivityId,
      note: data.note.trim() || undefined,
    };
    setNextStepsContext({
      task: followUpActivity,
      success: true,
      isBrokeragePipeline,
    });
    resetDialogState('completed');
  });

  const handleCancelledSubmit = handleSubmit(async data => {
    if (!effectiveActivityId) {
      resetDialogState();
      return;
    }

    currentOutcomeRef.current = 'cancelled';
    await submitOutcome({ success: false, data });
    const followUpActivity = activity ?? {
      id: effectiveActivityId,
      note: data.note.trim() || undefined,
    };
    setNextStepsContext({
      task: followUpActivity,
      success: false,
      isBrokeragePipeline,
    });

    resetDialogState('completed');
  });

  const handleNextStepClose = () => {
    setNextStepsContext(null);
    onClose();
  };

  const handleNextStepSelect = (action: ActivityNextStepAction) => {
    if (!nextStepsContext) {
      return;
    }

    const validActions: ActivityNextStepAction[] = [
      'schedule_visit',
      'schedule_call',
      'schedule_task',
      'lead_lost',
      'lead_won',
      'do_nothing',
    ];

    if (!validActions.includes(action)) {
      return;
    }

    // Store the selected action for the onClose handler
    selectedActionRef.current = action as VisitOutcomeFollowUpAction;

    onFollowUpActionSelect?.(action as VisitOutcomeFollowUpAction);

    setNextStepsContext(null);

    handleNextStepClose();
  };

  const followUpSuccess = nextStepsContext?.success ?? false;
  const followUpBrokerage = nextStepsContext?.isBrokeragePipeline ?? false;

  const palette = theme.palette;
  const nextStepOptions = useMemo(() => {
    return createActivityNextStepOptions({
      t,
      palette,
      canMarkLeadLost,
      followUpSuccess,
      followUpBrokerage,
      parentType,
    });
  }, [
    canMarkLeadLost,
    followUpBrokerage,
    followUpSuccess,
    palette,
    t,
    parentType,
  ]);

  const mandateOptions = useMemo<RaSelectOption[]>(() => {
    const rawOptions = data?.mandate_probability_types ?? [];
    const sortedOptions = [...rawOptions].sort((a, b) => {
      const aNumeric = parseNumeric(a.name) ?? parseNumeric(a.label);
      const bNumeric = parseNumeric(b.name) ?? parseNumeric(b.label);

      if (aNumeric != null && bNumeric != null) {
        return aNumeric - bNumeric;
      }

      return (a.label ?? '').localeCompare(b.label ?? '');
    });

    return sortedOptions.map(option => ({
      value: option.name,
      label: option.label ?? '',
    }));
  }, [data?.mandate_probability_types]);

  return (
    <>
      <SlideDialog
        open={open && autoOutcome == null && stage === 'outcome'}
        onClose={resetDialogState}
        title={t('How did the visit go?')}
        disableCancel
        customPrimaryActions={
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <Button
              disabled={isLoadingActivity}
              onClick={() => resetDialogState('cancelled')}
            >
              {t('Cancel')}
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => handleOutcomeSelection('cancelled')}
              loading={isLoadingActivity}
              loadingPosition="start"
            >
              {t('Visit cancelled')}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => handleOutcomeSelection('completed')}
              loading={isLoadingActivity}
              loadingPosition="start"
            >
              {t('Visit done')}
            </Button>
          </Box>
        }
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body1">
            {t('Log the outcome of your visit to keep the feed up to date.')}
          </Typography>
        </Box>
      </SlideDialog>
      <SlideDialog
        open={open && stage === 'completed' && nextStepsContext == null}
        onClose={resetDialogState}
        title={t('Visit done')}
        onSave={handleCompletedSubmit}
        loading={loading}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Controller
              name="note"
              control={control}
              rules={{
                validate: value => {
                  const trimmed = (value ?? '').trim();
                  return trimmed.length > 0 || t('This field is required');
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <FormControl fullWidth error={!!error}>
                  <MarkdownComposer
                    placeholder={t('What happened during the visit?')}
                    initialValue={value}
                    format="html"
                    preserveLineBreaks
                    readOnly={loading}
                    minHeight="130px"
                    onChange={onChange}
                  />
                  {error && (
                    <FormHelperText error={!!error}>
                      {error.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Box>
          {shouldShowWinFields && (
            <Stack
              sx={{
                gap: 2,
              }}
            >
              <Typography variant="subtitle1">
                {t('Can we sign the mandate? When?')}
              </Typography>
              <Box
                sx={{
                  gap: 2,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Box sx={{ flex: 1, width: '100%' }}>
                  <RaSelect
                    name="win_probability"
                    label={t('Mandate probability')}
                    control={control}
                    options={mandateOptions}
                    disabled={loading}
                  />
                </Box>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <RaDate
                    control={control}
                    name="predicted_win_date"
                    label={t('Predicted listing date')}
                    disabled={isPredictedDateDisabled}
                  />
                </Box>
              </Box>
            </Stack>
          )}
        </Box>
      </SlideDialog>
      <SlideDialog
        open={open && stage === 'cancelled' && nextStepsContext == null}
        onClose={resetDialogState}
        title={t('Visit cancelled')}
        onSave={handleCancelledSubmit}
        loading={loading}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Controller
              name="note"
              control={control}
              rules={{
                validate: value => {
                  const trimmed = (value ?? '').trim();
                  return trimmed.length > 0 || t('This field is required');
                },
              }}
              render={({
                field: { value, onChange },
                fieldState: { error },
              }) => (
                <>
                  <MarkdownComposer
                    placeholder={t('Why was the visit cancelled?')}
                    initialValue={value}
                    format="html"
                    preserveLineBreaks
                    readOnly={loading}
                    minHeight="130px"
                    onChange={onChange}
                  />
                  {error && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'error.main', display: 'block', mt: 0.5 }}
                    >
                      {error.message}
                    </Typography>
                  )}
                </>
              )}
            />
          </Box>
        </Box>
      </SlideDialog>
      <MutationErrorSnackbar
        error={errorLike}
        onClose={() => setErrorLike(null)}
      />
      <ActivityNextStepsDialog
        open={Boolean(nextStepsContext)}
        onClose={createNextStepsCloseHandler(
          selectedActionRef,
          handleNextStepClose,
        )}
        title={
          followUpSuccess ? t('Visit done') : t('Visit cancellation recorded')
        }
        options={nextStepOptions}
        onSelect={handleNextStepSelect}
      />
    </>
  );
};
